<template>
  <div class="aks-wrap">
    <LgInput
      :model-value="modelValue"
      class="aks-input"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      @update:model-value="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />

    <div
      v-if="open"
      class="aks-panel"
      role="listbox"
      :aria-label="`${ariaLabel ?? 'key'} suggestions`"
    >
      <div v-if="loading" class="aks-note">Loading…</div>
      <div v-else-if="error" class="aks-note aks-err">Couldn't load attribute keys.</div>
      <div v-else-if="!keys.length" class="aks-note">No attributes seen for this container.</div>
      <div v-else-if="!filtered.length" class="aks-note">No matching attributes.</div>
      <ul v-else class="aks-list">
        <li v-for="k in filtered" :key="k.key">
          <!-- mousedown.prevent keeps focus on the input (no blur), so the click still
               registers and we can close the panel ourselves in pick(). -->
          <button type="button" class="aks-item" @mousedown.prevent @click="pick(k.key)">
            <span class="aks-key">{{ k.key }}</span>
            <span class="aks-count">{{ k.count }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { api, type AttrKeyInfo } from '../api.js';
import { RANGE_MS } from '../composables/useFilters.js';
import LgInput from '../ui/LgInput.vue';

/**
 * Attr-key suggestion input for the App-settings default-pins editor — same
 * discover-then-filter idea as `AttrFilterDropdown`'s key list, scoped to a
 * single container. Free-form typing is always allowed (the server validates
 * shape/length); this only offers a shortcut for keys we've actually seen.
 *
 * Fetch-on-focus + client-side filter (not fetch-per-keystroke): the key list
 * for a container is fetched once, cached for the component's lifetime, and
 * every keystroke after that just filters the cached list.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: string;
    container: string;
    placeholder?: string;
    ariaLabel?: string;
    /** Keys already pinned for this container — hidden from suggestions since
     * picking one again would just be a no-op (or silently clear the draft). */
    excludeKeys?: string[];
  }>(),
  // Mirrors LgInput's own `modelValue` default: a reactive draft object (e.g.
  // SettingsView's per-container `draftKey`) reads as `undefined` until its
  // first write, so this must tolerate that rather than assume a string.
  { modelValue: '' },
);

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const open = ref(false);
const loading = ref(false);
const loaded = ref(false);
const error = ref(false);
const keys = ref<AttrKeyInfo[]>([]);

const filtered = computed(() => {
  const excluded = new Set(props.excludeKeys ?? []);
  const q = props.modelValue.trim().toLowerCase();
  return keys.value.filter((k) => !excluded.has(k.key) && (!q || k.key.toLowerCase().includes(q)));
});

function onInput(value: string) {
  emit('update:modelValue', value);
}

function onFocus() {
  open.value = true;
  if (!loaded.value && !loading.value) void loadKeys();
}

function onBlur() {
  // Suggestion clicks use mousedown.prevent so they never trigger this blur;
  // any other blur (click elsewhere, tab away) closes the panel.
  open.value = false;
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) {
    e.preventDefault();
    open.value = false;
  }
}

function pick(key: string) {
  emit('update:modelValue', key);
  open.value = false;
}

async function loadKeys() {
  loading.value = true;
  error.value = false;
  try {
    // A broad recent window — this is a one-off admin lookup, not a live tail,
    // so favor recall of keys the container has actually emitted over recency.
    const to = Date.now();
    const from = to - RANGE_MS['7d'];
    const res = await api.attrKeys({
      containers: props.container,
      from: String(from),
      to: String(to),
    });
    keys.value = res.keys;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
    loaded.value = true;
  }
}
</script>

<style scoped>
.aks-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}
.aks-input {
  width: 100%;
}
.aks-panel {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}
.aks-note {
  padding: var(--lg-space-2);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}
.aks-err {
  color: var(--lg-danger);
}
.aks-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.aks-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-2);
  width: 100%;
  padding: var(--lg-space-1) var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.aks-item:hover {
  background: var(--lg-bg-hover);
}
.aks-key {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aks-count {
  flex-shrink: 0;
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-2xs);
}
</style>
